// src/components/assessment/InteractiveRadar.jsx

import React, { useState, useEffect, useRef } from 'react';

const METRICS = [
    { id: 'focus', label: 'Focus' },
    { id: 'resilience', label: 'Resilience' },
    { id: 'motivation', label: 'Motivation' },
    { id: 'confidence', label: 'Confidence' },
    { id: 'discipline', label: 'Discipline' },
    { id: 'awareness', label: 'Awareness' },
    { id: 'adaptability', label: 'Adaptability' },
    { id: 'sleep_perf', label: 'Sleep' },
    { id: 'nutrition', label: 'Fuel' },
    { id: 'injury_mgmt', label: 'Pain Mgmt' }
];

// Configuration for scaled containment
const CENTER_X = 150;
const CENTER_Y = 175; 
const RADIUS = 80;    
const SVG_VIEWBOX = "0 -10 300 345";

const InteractiveRadar = ({ initialValues, onUpdate }) => {
    const [scores, setScores] = useState(initialValues || {});
    const [activePoint, setActivePoint] = useState(null);
    const svgRef = useRef(null);

    useEffect(() => {
        if (!initialValues || Object.keys(initialValues).length === 0) {
            const defaults = {};
            METRICS.forEach(m => defaults[m.id] = 50);
            setScores(defaults);
        }
    }, [initialValues]);

    // Math Helpers for Radar Geometry
    const getCoordinates = (value, index, total) => {
        const angle = (Math.PI * 2 * index) / total - (Math.PI / 2);
        const r = (value / 100) * RADIUS;
        const x = CENTER_X + Math.cos(angle) * r;
        const y = CENTER_Y + Math.sin(angle) * r;
        return { x, y };
    };

    const getPolyPath = (currentScores) => {
        return METRICS.map((m, i) => {
            const val = currentScores[m.id] || 50;
            const { x, y } = getCoordinates(val, i, METRICS.length);
            return `${x},${y}`;
        }).join(" ");
    };

    // --- NEW: MATHEMATICAL HIT DETECTION ---
    const handlePointerDown = (e) => {
        const svg = svgRef.current;
        if (!svg) return;

        // 1. Get exact SVG coordinates of the click/touch
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

        let closestId = null;
        let minDistance = Infinity;

        // 2. Measure the distance to EVERY point on the chart
        METRICS.forEach((m, i) => {
            const val = scores[m.id] || 50;
            const { x, y } = getCoordinates(val, i, METRICS.length);
            const dist = Math.sqrt(Math.pow(svgP.x - x, 2) + Math.pow(svgP.y - y, 2));

            if (dist < minDistance) {
                minDistance = dist;
                closestId = m.id;
            }
        });

        // 3. If they clicked within a reasonable distance (35 SVG units) of a point, grab the closest one!
        if (minDistance <= 35) {
            setActivePoint(closestId);
            e.target.setPointerCapture(e.pointerId); // Locks the drag event to the SVG even if finger slips outside
        }
    };

    const handlePointerMove = (e) => {
        if (!activePoint) return;

        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
        const dx = svgP.x - CENTER_X;
        const dy = svgP.y - CENTER_Y;
        
        let dist = Math.sqrt(dx*dx + dy*dy);
        let newVal = (dist / RADIUS) * 100;
        
        if (newVal > 100) newVal = 100;
        if (newVal < 10) newVal = 10;

        setScores(prev => {
            const updated = { ...prev, [activePoint]: Math.round(newVal) };
            if (onUpdate) onUpdate(updated);
            return updated;
        });
    };

    const handlePointerUp = (e) => {
        setActivePoint(null);
        if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
            e.target.releasePointerCapture(e.pointerId);
        }
    };

    return (
        <div className="w-full h-full flex items-center justify-center p-2 select-none touch-none">
            <svg 
                ref={svgRef}
                viewBox={SVG_VIEWBOX}
                preserveAspectRatio="xMidYMid meet"
                className="w-full h-auto max-w-[460px] max-h-full drop-shadow-2xl cursor-crosshair"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                style={{ touchAction: 'none' }} // Prevents browser scrolling while dragging
            >
                {/* 1. INTERNAL TITLE */}
                <text x={CENTER_X} y="15" textAnchor="middle" className="text-[22px] font-black fill-slate-900 uppercase tracking-tight">Performance Radar</text>
                
                {/* 2. LEGEND WITH UPDATED LABELS: Centered between title and chart area */}
                <g transform={`translate(${CENTER_X - 95}, 30)`}>
                    <rect x="-9" y="0" width="97" height="18" rx="4" className="fill-red-50 stroke-red-100" />
                    <text x="40" y="12" textAnchor="middle" className="text-[8.5px] font-black fill-red-500 uppercase tracking-wider">Inner: Struggling</text>
                    
                    <line x1="90" y1="9" x2="100" y2="9" className="stroke-slate-300" strokeWidth="1.5" />
                    
                    <rect x="104" y="0" width="86" height="18" rx="4" className="fill-emerald-50 stroke-emerald-100" />
                    <text x="147" y="12" textAnchor="middle" className="text-[8.5px] font-black fill-emerald-600 uppercase tracking-wider">Outer: Thriving</text>
                </g>

                {/* 3. BACKGROUND GRID */}
                {[20, 40, 60, 80, 100].map(level => (
                    <circle 
                        key={level} 
                        cx={CENTER_X} cy={CENTER_Y} r={(level / 100) * RADIUS} 
                        fill="none" 
                        stroke={level === 100 ? "#10b981" : level === 20 ? "#ef4444" : "#e2e8f0"} 
                        strokeWidth={level === 100 || level === 20 ? "1.2" : "0.8"} 
                        strokeDasharray={level === 100 || level === 20 ? "0" : "3 3"}
                    />
                ))}

                {/* 4. AXIS LINES */}
                {METRICS.map((m, i) => {
                    const { x, y } = getCoordinates(100, i, METRICS.length);
                    return <line key={i} x1={CENTER_X} y1={CENTER_Y} x2={x} y2={y} stroke="#cbd5e1" strokeWidth="0.8" />;
                })}

                {/* 5. DATA POLYGON */}
                <polygon 
                    points={getPolyPath(scores)} 
                    fill="rgba(6, 121, 175, 0.2)" 
                    stroke="#0679af" 
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    className="pointer-events-none"
                />

                {/* 6. HANDLES & LABELS */}
                {METRICS.map((m, i) => {
                    const val = scores[m.id] || 50;
                    const { x, y } = getCoordinates(val, i, METRICS.length);
                    
                    // Buffer increased to prevent label overlap with chart
                    const labelPos = getCoordinates(140, i, METRICS.length);

                    return (
                        <g key={m.id} className="pointer-events-none">
                            <text 
                                x={labelPos.x} y={labelPos.y + 3} 
                                textAnchor="middle" 
                                className={`text-[9px] font-black uppercase transition-colors duration-200
                                    ${activePoint === m.id ? 'fill-brand-primary' : 'fill-slate-500'}`}
                            >
                                {m.label}
                            </text>

                            {activePoint === m.id && (
                                <g transform={`translate(${x}, ${y - 20})`}>
                                    <rect x="-12" y="-10" width="24" height="16" rx="4" fill="#0679af" />
                                    <text textAnchor="middle" y="2" fill="white" fontSize="9" fontWeight="900">{Math.round(val)}</text>
                                </g>
                            )}

                            <circle
                                cx={x} cy={y} r={activePoint === m.id ? 9 : 6}
                                fill={activePoint === m.id ? "#fff" : "#0679af"}
                                stroke="#0679af" strokeWidth={activePoint === m.id ? 3 : 2}
                                className="transition-all duration-75"
                            />
                        </g>
                    );
                })}
                
                <text x={CENTER_X} y="322" textAnchor="middle" className="text-[8px] font-bold fill-slate-400 uppercase tracking-widest opacity-60 pointer-events-none">Map state to continue</text>
            </svg>
        </div>
    );
};

export default InteractiveRadar;